// KpiGridRow değer hücresi (`<td role="gridcell">`) — KpiGridRow.tsx'ten salt taşıma (≤600 satır
// kuralı; kırılım satır türleri o dosyada büyüdü). Durum pili, amber nokta, ⓘ, RAPORLANAN/SABİT,
// FORM, not ve kanıt rozetleri; rozetler `<button>` — hücre tıklaması editörü açmasın diye
// `stopPropagation`.

import {
  cellFootnoteKey,
  formatKpiNumber,
  isPartialKpiCell,
  type KpiNoteSummaryCell,
} from "@mahir/shared";
import { KpiCellNoteBadge } from "./KpiCellNoteBadge";
import { KpiFootnoteMark } from "./KpiFootnoteList";
import { CellStatusNote, PartialInfoButton, TD_BORDER } from "./KpiGridRowParts";
import type { KpiGridRowProps } from "./KpiGridRow";
import type { GridColumn } from "../../lib/kpi-grid-nav";
import { isTotalColumn, readCell } from "../../lib/kpi-grid-nav";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import { noteCellKey, type GridNotes } from "../../lib/kpi-notes-ui";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { breakdownMismatch, totalCellState, totalCellView } from "../../lib/kpi-grid-nav";
import { formatKpiCell, KPI_COPY } from "../../lib/kpi-ui";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";

/** Not yuvası olan hücrenin sağ dolgusu (px): 10 px dolgu + boş not simgesi (globals.css 18 px). */
const NOTE_SLOT_PADDING = 30;

const badgeBtn: React.CSSProperties = {
  display: "inline-block",
  marginLeft: 6,
  padding: "0 6px",
  borderRadius: 999,
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: "16px",
  verticalAlign: "middle",
  cursor: "pointer",
  minHeight: 18,
};

export function KpiGridValueCell(props: {
  p: KpiGridRowProps;
  col: GridColumn;
  ci: number;
  rowReadonly: boolean;
  firstTotalIndex: number;
}): JSX.Element {
  const { p, col, ci, rowReadonly, firstTotalIndex } = props;
  const {
    row,
    rowIndex,
    focus,
    readOnly,
    saveErrors,
    cellRefs,
    onFocusCell,
    onActivate,
    onOpenEvidence,
    formMismatchFor,
    onApplyFormValue,
    onOpenReported,
    clientView,
    notes,
    onOpenNotes,
  } = p;
  const ind = row.indicator;
  const cell = readCell(ind, col);
  const d = formatKpiCell(cell, { percent: ind.valueType === "percent" });
  // kırılım toplamı (DR4): hesaplanamayan durum soluk "—" (asla kırmızı) + canlı bölge + uyuşmazlık
  const isTotal = row.kind === "total";
  // (3) hesap grubu başka sette: bu sette grupsuz satır da durum taşır (totalCellState kararı)
  const bdState = totalCellState(row, cell);
  // DR4 bekleme: hesap grubu üyesinin kaydı sürerken toplam hücresi soluk + aria-busy
  const recomputing = isTotal && !!p.isRecomputing?.(ind.id, col);
  const bdView =
    bdState && cell?.status === "calc_error" ? totalCellView(bdState, !!clientView) : null;
  const bdCopy = clientView ? KPI_BREAKDOWN_COPY.portal : KPI_BREAKDOWN_COPY;
  const pos = { row: rowIndex, col: ci };
  const focused = focus?.row === rowIndex && focus.col === ci;
  const editable =
    !readOnly &&
    !ind.isDerived &&
    row.total?.derived !== true &&
    !col.aggregate &&
    !rowReadonly &&
    !(clientView && cell?.editable === false);
  const errKey = col.entityId ? `${ind.metricId}|${col.entityId}|${col.year}` : "";
  const err = errKey ? saveErrors[errKey] : undefined;
  const color = bdView
    ? bdView.amber
      ? "var(--color-warning-text)"
      : "var(--color-text-3)"
    : d.tone === "pending"
      ? "var(--color-warning-text)"
      : d.tone === "error"
        ? "var(--color-error)"
        : d.tone === "empty"
          ? "var(--color-text-3)"
          : d.tone === "text"
            ? "var(--color-text-2)"
            : "var(--color-text)";
  const evidenceCount = cell?.evidenceCount ?? 0;
  const reported = cell?.isReported === true && !col.aggregate;
  // DR4 (4) raporlanan yedek: rozet başlığı "raporlanan · kırılım eklendi" (yalnız danışman)
  const reportedTitle = cell?.pinned
    ? KPI_EVIDENCE_COPY.reported.pinnedTitle
    : bdState?.state === "reported_fallback" && !clientView
      ? KPI_BREAKDOWN_COPY.states.reported_fallback.badgeTitle
      : KPI_EVIDENCE_COPY.reported.badgeTitle;
  const formMismatch =
    !col.aggregate && !row.indicator.isDerived
      ? formMismatchFor?.(row.indicator.metricId, col.year)
      : undefined;
  // T12 fix 1 (I1): not yuvası olan hücrede sağ dolgu boş not simgesine ayrılır — simge köşede
  // içerikle (değer, 📎, FORM, raporlanan) hiç çakışmaz; sütunda sayılar yine hizalı.
  const noteSlot = Boolean(notes && col.entityId && !col.aggregate);
  return (
    <td
      ref={(el) => {
        if (el) cellRefs.current.set(`${rowIndex}:${ci}`, el);
        else cellRefs.current.delete(`${rowIndex}:${ci}`);
      }}
      role="gridcell"
      // Finar rev-B 3 — türetilmiş hücre: hover/odak kartı (use-formula-hover, delegasyon)
      {...(ind.isDerived ? { "data-formula-cell": `${rowIndex}:${ci}` } : {})}
      tabIndex={focused ? 0 : -1}
      aria-readonly={!editable}
      aria-busy={recomputing ? true : undefined}
      onFocus={() => onFocusCell(pos)}
      onClick={() => onActivate(pos)}
      onDoubleClick={() => onActivate(pos)}
      title={
        err ??
        (cell?.workflowState && cell.workflowState !== "verified"
          ? KPI_COPY.workflow[cell.workflowState as keyof typeof KPI_COPY.workflow]
          : // portal: hesaplanan toplam salt okunur — tek genel kopya (spec §6.5)
            clientView && isTotal && row.total?.derived === true
            ? KPI_BREAKDOWN_COPY.portal.autoComputed
            : undefined)
      }
      style={{
        padding: noteSlot ? `6px ${NOTE_SLOT_PADDING.toString()}px 6px 10px` : "6px 10px",
        borderBottom: TD_BORDER,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
        fontSize: "0.85rem",
        color,
        opacity: recomputing ? 0.6 : undefined,
        background: col.aggregate ? "var(--color-bg)" : undefined,
        // toplam sütun grubunun ilk hücresi: gruba ayırıcı çizgi (başlıkla aynı)
        ...(isTotalColumn(col) && ci === firstTotalIndex
          ? { borderLeft: "1px solid var(--color-border)" }
          : {}),
        cursor: editable || ind.isDerived ? "pointer" : "default",
        position: "relative", // boş not simgesi sağ dolgudaki yuvaya yerleşir (globals.css)
        outline: focused
          ? "2px solid var(--color-primary)"
          : err
            ? "2px solid var(--color-error)"
            : "none",
        outlineOffset: -2,
        minHeight: 36,
        whiteSpace: "nowrap",
      }}
    >
      {isTotal ? (
        <span aria-live="polite" aria-atomic="true" data-testid="kpi-total-live">
          <span className="sr-only">
            {bdCopy.live(ind.label, col.label, bdView ? bdView.title : d.text)}
          </span>
          <span aria-hidden="true">
            {bdView ? (
              <span
                title={bdView.title}
                style={{ fontSize: bdView.text === "—" ? undefined : "0.75rem" }}
              >
                {bdView.text}
              </span>
            ) : (
              <CellValueText d={d} color={color} />
            )}
          </span>
        </span>
      ) : bdView ? (
        <span title={bdView.title}>{bdView.text}</span>
      ) : (
        <CellValueText d={d} color={color} />
      )}
      {isTotal && breakdownMismatch(cell) ? (
        <span
          data-testid="kpi-breakdown-mismatch"
          role="img"
          aria-label={
            clientView ? KPI_BREAKDOWN_COPY.portal.mismatch : KPI_BREAKDOWN_COPY.mismatchDot
          }
          title={clientView ? KPI_BREAKDOWN_COPY.portal.mismatch : KPI_BREAKDOWN_COPY.mismatchDot}
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--color-warning-text)",
            marginLeft: 5,
            verticalAlign: "middle",
          }}
        />
      ) : null}
      {col.entityId && !col.aggregate ? (
        <KpiFootnoteMark refKey={cellFootnoteKey(ind.id, col.entityId, col.year)} />
      ) : null}
      {clientView && !col.aggregate && !clientView.consolidated ? (
        <CellStatusNote ind={ind} cell={cell} />
      ) : null}
      {d.dot ? (
        <span
          // güvenlik/UX taraması 2026-10-08 (RK36): etiketler KPI_COPY.grid'den
          aria-label={cell?.unverified ? KPI_COPY.grid.dotPending : KPI_COPY.grid.dotPartial}
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--color-warning-text)",
            marginLeft: 5,
            verticalAlign: "middle",
          }}
        />
      ) : null}
      {clientView?.unitChanged?.[errKey] ? (
        <span
          data-testid="kpi-unit-changed-dot"
          role="img"
          aria-label={KPI_UNIT_COPY.portalDot}
          title={KPI_UNIT_COPY.portalDot}
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--color-warning-text)",
            marginLeft: 5,
            verticalAlign: "middle",
          }}
        />
      ) : null}
      {cell && p.partialLookup && isPartialKpiCell(cell) ? (
        <PartialInfoButton cell={cell} lookup={p.partialLookup} client={!!clientView} />
      ) : null}
      {cell?.workflowState === "submitted" || cell?.workflowState === "clarification" ? (
        <span
          className="ml-1"
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color:
              cell.workflowState === "submitted"
                ? "var(--color-warning-text)"
                : "var(--color-error)",
          }}
          title={KPI_COPY.workflow[cell.workflowState]}
        >
          ●
        </span>
      ) : null}
      {reported ? (
        <button
          type="button"
          data-testid="kpi-reported-badge"
          aria-label={reportedTitle}
          title={reportedTitle}
          onClick={(e) => {
            e.stopPropagation();
            onOpenReported(pos);
          }}
          style={{
            ...badgeBtn,
            border: cell?.pinned
              ? "1px solid var(--color-primary)"
              : "1px dashed var(--color-primary)",
            background: cell?.pinned
              ? "color-mix(in srgb, var(--color-primary) 10%, white)"
              : "transparent",
            color: "var(--color-primary)",
          }}
        >
          {cell?.pinned ? KPI_EVIDENCE_COPY.reported.pinnedBadge : KPI_EVIDENCE_COPY.reported.badge}
        </button>
      ) : null}
      {/* Finar 14.4 (K4): birim uyuşmazlığında rozet yok — sessiz yanlış birim yazımı olmaz */}
      {formMismatch && formMismatch.formValue !== null && formMismatch.kind !== "unit_mismatch" ? (
        <button
          type="button"
          data-testid="kpi-form-badge"
          aria-label={`Form yanıtı ${formatKpiNumber(formMismatch.formValue)} — form yanıtını al`}
          title={`Form yanıtı ${formatKpiNumber(formMismatch.formValue)} (${formMismatch.questionLabel}) — tıkla: form yanıtını al`}
          onClick={(e) => {
            e.stopPropagation();
            onApplyFormValue?.(formMismatch);
          }}
          style={{
            ...badgeBtn,
            border: "1px solid var(--color-warning-border, var(--color-border))",
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
          }}
        >
          FORM
        </button>
      ) : null}
      {notes && col.entityId && !col.aggregate ? (
        <NoteBadgeFor
          notes={notes}
          summary={notes.summary.get(
            noteCellKey({ metricId: ind.metricId, entityId: col.entityId, year: col.year }),
          )}
          onOpen={() => onOpenNotes?.(pos)}
        />
      ) : null}
      {evidenceCount > 0 ? (
        <button
          type="button"
          data-testid="kpi-evidence-count"
          aria-label={`${evidenceCount.toString()} kanıt — Kanıt sekmesini aç`}
          title={`${evidenceCount.toString()} kanıt`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenEvidence(pos);
          }}
          style={{
            ...badgeBtn,
            border: "1px solid transparent",
            background: "transparent",
            color: "var(--color-text-3)",
            fontSize: "0.7rem",
            letterSpacing: 0,
          }}
        >
          <span aria-hidden="true">📎</span> {evidenceCount}
        </button>
      ) : null}
    </td>
  );
}

function NoteBadgeFor(p: {
  notes: GridNotes;
  summary: KpiNoteSummaryCell | undefined;
  onOpen: () => void;
}): JSX.Element {
  return (
    <KpiCellNoteBadge
      audience={p.notes.audience}
      count={p.summary?.count ?? 0}
      unread={p.summary?.unread ?? 0}
      onOpen={p.onOpen}
    />
  );
}

/** Değer metni: XXX / HESAPLANAMADI durum pili ya da düz metin (hücre ve canlı bölge ortak). */
function CellValueText(props: { d: ReturnType<typeof formatKpiCell>; color: string }): JSX.Element {
  const { d, color } = props;
  if (d.tone !== "pending" && d.tone !== "error") return <>{d.text}</>;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: "0.68rem",
        fontWeight: 700,
        background:
          d.tone === "pending"
            ? "var(--color-warning-bg)"
            : "color-mix(in srgb, var(--color-error) 10%, white)",
        border:
          d.tone === "pending" ? "1px solid var(--color-warning-border)" : "1px solid transparent",
        color,
      }}
    >
      {d.text}
    </span>
  );
}
